import { InfoOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { BaseAutocomplete } from "@/components/a-autocomplete";
import { useTableSettings } from "@/hooks/use-table-settings";
import { useApi } from "@/shared/api/use-api";

type Column = {
  requested_value: string;
  internal_id?: string;
  external_id?: string;
  nick?: string;
};

export const useGetAAClubMapping = (ids: string[], salt: number | null) => {
  const { sysDataCommonApi } = useApi();

  return useQuery({
    queryKey: [`/api/v2/sys-data/aa_eco/clubs_ids_mapping`, salt],
    queryFn: ({ signal }) => sysDataCommonApi.getAAClubIdsMapping(new Set(ids), { signal }),
    enabled: !!salt && !!ids.length,
  });
};

export const ClubIdMappingTable = () => {
  const { t } = useTranslation();
  const [ids, setIds] = useState<string[]>([]);
  const [options, setOptions] = useState<string[]>([]);
  const [salt, setSalt] = useState<null | number>(null);

  const handleOptionArrayKeyDown =
    <T = number,>(
      options: T[],
      setOptions: Dispatch<SetStateAction<T[]>>,
      setValue: Dispatch<SetStateAction<T[]>> | ((value: Array<T>) => void),
      currentValue: T[],
      type: "string" | "number"
    ) =>
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        const newValue = (event.target as HTMLInputElement).value;
        const isNumber = type === "number";
        const value = isNumber ? parseInt(newValue, 10) : newValue;
        if (isNumber && isNaN(value as number)) {
          return;
        }
        if (!options.includes(value as T)) {
          setOptions((prevState) => {
            return [...prevState, value] as T[];
          });
        }
        if (!currentValue.includes(value as T)) {
          setValue(Array.from(new Set([...currentValue, value])) as T[]);
        }
      }
    };

  const { data: response, isLoading } = useGetAAClubMapping(ids, salt);

  const data = useMemo(() => {
    if (response?.data) {
      return Object.keys(response.data).map((key) => {
        return {
          requested_value: key,
          ...response.data[key as keyof typeof response.data],
        };
      });
    }
    return [];
  }, [response]);

  const columns = useMemo<MRT_ColumnDef<Column>[]>(
    () => [
      {
        header: t("requestedValue"),
        accessorKey: "requested_value",
        filterFn: "contains",
      },
      {
        header: t("internalId"),
        accessorKey: "internal_id",
        filterFn: "contains",
      },
      {
        header: t("externalId"),
        accessorKey: "external_id",
        filterFn: "contains",
      },
      {
        header: t("name"),
        accessorKey: "name",
        filterFn: "contains",
      },
    ],
    [t]
  );

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<Column>("aaClubIdMapping");

  const table = useMaterialReactTable({
    data,
    columns: columns,
    getRowId: (originalRow) => originalRow.requested_value,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "internalId", desc: false }],
    },
    enablePagination: true,
    enableStickyHeader: true,
    enableSorting: true,
    state: {
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "internalId", desc: false }],
      isLoading,
    },
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <Stack
      component="form"
      onSubmit={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setSalt(+new Date());
      }}
      sx={{ mt: 2, maxWidth: "600px" }}
    >
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
        <BaseAutocomplete
          onChange={(_event, newValue) => {
            setIds(newValue);
          }}
          value={ids}
          options={options}
          multiple
          sx={{ minWidth: "400px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t("idOrName")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <Tooltip title={t("Enter a value and press Enter to add it")}>
                      <IconButton size="small" tabIndex={-1}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              variant="outlined"
              onKeyDown={handleOptionArrayKeyDown(options, setOptions, setIds, ids, "string")}
            />
          )}
        />
        <Button type="submit" variant="contained">
          {t("submit")}
        </Button>
      </Box>
      {!!salt && <MaterialReactTable table={table} />}
    </Stack>
  );
};
